// src/pages/GestioneFatturazione.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Card, TableCell, TableHeadCell } from '../components/ui';

const API_BASE = '/api';

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

// 🔹 helper: parsing codice interno (I25-00000-2 -> base + iterazione)
const parseInternalCode = (codeStr) => {
  if (!codeStr) return { baseCode: '', iteration: 0 };
  const trimmed = String(codeStr).trim();
  const parts = trimmed.split('-');
  const last = parts[parts.length - 1];
  if (/^\d+$/.test(last)) {
    const iteration = Number(last);
    const baseCode = parts.slice(0, -1).join('-');
    return { baseCode, iteration };
  }
  return { baseCode: trimmed, iteration: 0 };
};

// 🔹 mappa intestazioni Excel -> colonne
const buildHeaderMap = (headers) => {
  const map = {};
  headers.forEach((h, idx) => {
    const name = String(h || '').toLowerCase();
    if (!name) return;

    // Codice interno
    if (!map.internalCode && name.includes('cod') && name.includes('intern')) {
      map.internalCode = idx;
    }
    // Codice esterno
    else if (!map.externalCode && name.includes('cod') && name.includes('estern')) {
      map.externalCode = idx;
    }
    // Cliente / Azienda
    else if (
      !map.companyName &&
      (name.includes('cliente') || name.includes('azienda'))
    ) {
      map.companyName = idx;
    }
    // Luogo / sede
    else if (
      !map.place &&
      (name.includes('luogo') ||
        name.includes('sede') ||
        name.includes('local') ||
        name.includes('citt'))
    ) {
      map.place = idx;
    }
    // Modello
    else if (
      !map.equipmentModel &&
      (name.includes('modello') || name.includes('model'))
    ) {
      map.equipmentModel = idx;
    }
    // Importo consuntivo
    else if (!map.importoConsuntivo && name.includes('consunt')) {
      map.importoConsuntivo = idx;
    }
    // Importo preventivo
    else if (!map.importoPreventivo && name.includes('prevent')) {
      map.importoPreventivo = idx;
    }
    // Numero preventivo
    else if (
      !map.numeroPreventivo &&
      name.includes('num') &&
      name.includes('prev')
    ) {
      map.numeroPreventivo = idx;
    }
    // Importo ordine
    else if (
      !map.importoOrdine &&
      name.includes('importo') &&
      name.includes('ordine')
    ) {
      map.importoOrdine = idx;
    }
    // Riferimenti ordine cliente
    else if (
      !map.riferimentiOrdineCliente &&
      (name.includes('rif') || name.includes('rifer')) &&
      name.includes('ordine') &&
      name.includes('client')
    ) {
      map.riferimentiOrdineCliente = idx;
    }
    // Stato pagamento
    else if (
      !map.stato &&
      (name.includes('stato pag') ||
        (name.includes('stato') && name.includes('pagamento')))
    ) {
      map.stato = idx;
    }
    // Ordine fornitore
    else if (
      !map.ordineFornitore &&
      (name.includes('ordine for') || name.includes('ord for'))
    ) {
      map.ordineFornitore = idx;
    }
    // Data ODA fornitura
    else if (
      !map.dataOdaFornitura &&
      name.includes('data') &&
      name.includes('oda')
    ) {
      map.dataOdaFornitura = idx;
    }
  });
  return map;
};


const renderOrderStatusBadge = (status) => {
  if (!status) return <span className="text-gray-400 text-xs">-</span>;

  let cls = 'bg-gray-100 text-gray-700 border border-gray-200';

  if (status === 'Ordine ricevuto') {
    cls = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  } else if (status === 'Ordine non ricevuto') {
    cls = 'bg-amber-50 text-amber-700 border-amber-200';
  } else if (status === 'Nessun ordine') {
    cls = 'bg-gray-50 text-gray-600 border border-gray-200';
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}
    >
      {status}
    </span>
  );
};

const renderPreventivoStatusBadge = (status) => {
  if (!status) return <span className="text-gray-400 text-xs">-</span>;

  const lower = status.toLowerCase();
  let cls = 'bg-gray-100 text-gray-700 border border-gray-200';

  if (
    lower.includes('accett') ||
    lower.includes('approv') ||
    lower.includes('fatto') ||
    lower.includes('contratto')
  ) {
    cls = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  } else if (
    lower.includes('da fare') ||
    lower.includes('da inviare') ||
    lower.includes('inviato') ||
    lower.includes('in attesa') ||
    lower.includes('consuntivo')
  ) {
    cls = 'bg-amber-50 text-amber-700 border border-amber-200';
  } else if (lower.includes('rifiut') || lower.includes('negat')) {
    cls = 'bg-red-50 text-red-700 border border-red-200';
  }

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}
    >
      {status}
    </span>
  );
};

const GestioneFatturazione = () => {
  const navigate = useNavigate();
  const [role] = useState(localStorage.getItem('role') || null);

  const canAccess =
    role === 'admin' || role === 'gestione' || role === 'segreteria';

  useEffect(() => {
    if (!canAccess) {
      navigate('/');
    }
  }, [canAccess, navigate]);

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState('');

  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [detailsRows, setDetailsRows] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  const [editingOrder, setEditingOrder] = useState(null);
  const [editingIteration, setEditingIteration] = useState(null); // null = riga madre
  const [editingIterationCode, setEditingIterationCode] = useState('');
  const [billingForm, setBillingForm] = useState({
    importoConsuntivo: '',
    importoPreventivo: '',
    numeroPreventivo: '',
    importoOrdine: '',
    riferimentiOrdineCliente: '',
    stato: '',
    ordineFornitore: '',
    dataOdaFornitura: '',
    materialeArrivato: '',
  });

  const [deleteOrder, setDeleteOrder] = useState(null);
  const [deleteCode, setDeleteCode] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // eliminazione singola riassegnazione
  const [deleteDetailOrder, setDeleteDetailOrder] = useState(null);
  const [deleteDetailRow, setDeleteDetailRow] = useState(null);
  const [deleteDetailCode, setDeleteDetailCode] = useState('');
  const [deleteDetailError, setDeleteDetailError] = useState('');


  // stato per import Excel
  const [importing, setImporting] = useState(false);

  // -------- CARICAMENTO COMMESSE --------
  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE}/work/billing`);
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Errore nel caricamento fatture.');
      }
      const data = await res.json();
      setOrders(data || []);
    } catch (err) {
      console.error('Errore loadOrders (fatture):', err);
      setError(err.message || 'Errore imprevisto nel caricamento fatture.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  // -------- FILTRO LOCALE (search + stato pagamento) --------
  const filteredOrders = useMemo(() => {
    const text = (search || '').toLowerCase().trim();

    return orders.filter((o) => {
      const matchText =
        (o.internalCode || '').toLowerCase().includes(text) ||
        (o.externalCode || '').toLowerCase().includes(text) ||
        (o.companyName || '').toLowerCase().includes(text) ||
        (o.place || '').toLowerCase().includes(text) ||
        (o.equipmentModel || '').toLowerCase().includes(text);

      const matchStatoPagamento = filterStato
        ? (o.billing?.stato || '') === filterStato
        : true;

      return matchText && matchStatoPagamento;
    });
  }, [orders, search, filterStato]);

  // -------- EDIT DATI CONTABILI --------
  const openEdit = (order, detailRow = null) => {
    const b = order.billing || {};
    setEditingOrder(order);

    if (detailRow) {
      setEditingIteration(detailRow.iteration);
      setEditingIterationCode(
        detailRow.codiceInterno || order.internalCode || ''
      );

      setBillingForm({
        importoConsuntivo:
          detailRow.importoConsuntivo !== undefined &&
          detailRow.importoConsuntivo !== null
            ? String(detailRow.importoConsuntivo)
            : '',
        importoPreventivo:
          detailRow.importoPreventivo !== undefined &&
          detailRow.importoPreventivo !== null
            ? String(detailRow.importoPreventivo)
            : '',
        numeroPreventivo: b.numeroPreventivo || order.rifPreventivo || '',
        importoOrdine:
          detailRow.importoOrdine !== undefined &&
          detailRow.importoOrdine !== null
            ? String(detailRow.importoOrdine)
            : '',
        riferimentiOrdineCliente: b.riferimentiOrdineCliente || '',
        stato: b.stato || '',
        ordineFornitore: b.ordineFornitore || '',
        dataOdaFornitura: b.dataOdaFornitura
          ? formatDate(b.dataOdaFornitura)
          : '',
        materialeArrivato: order.materialeArrivato || '',
      });
    } else {
      setEditingIteration(null);
      setEditingIterationCode(order.internalCode || '');
      setBillingForm({
        importoConsuntivo:
          b.importoConsuntivo !== undefined && b.importoConsuntivo !== null
            ? String(b.importoConsuntivo)
            : '',
        importoPreventivo:
          b.importoPreventivo !== undefined && b.importoPreventivo !== null
            ? String(b.importoPreventivo)
            : '',
        numeroPreventivo: b.numeroPreventivo || order.rifPreventivo || '',
        importoOrdine:
          b.importoOrdine !== undefined && b.importoOrdine !== null
            ? String(b.importoOrdine)
            : '',
        riferimentiOrdineCliente: b.riferimentiOrdineCliente || '',
        stato: b.stato || '',
        ordineFornitore: b.ordineFornitore || '',
        dataOdaFornitura: b.dataOdaFornitura
          ? formatDate(b.dataOdaFornitura)
          : '',
        materialeArrivato: order.materialeArrivato || '',
      });
    }
  };

  const closeEdit = () => {
    setEditingOrder(null);
    setEditingIteration(null);
    setEditingIterationCode('');
    setBillingForm({
      importoConsuntivo: '',
      importoPreventivo: '',
      numeroPreventivo: '',
      importoOrdine: '',
      riferimentiOrdineCliente: '',
      stato: '',
      ordineFornitore: '',
      dataOdaFornitura: '',
      materialeArrivato: '',
    });
  };

  const handleSave = async () => {
    if (!editingOrder) return;
    setError('');
    setSuccess('');

    try {
      const payload = { ...billingForm };
      if (editingIteration !== null && editingIteration !== undefined) {
        payload.iteration = editingIteration;
      }

      const res = await authFetch(
        `${API_BASE}/work/${editingOrder._id}/billing`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore nel salvataggio dati contabili.'
        );
      }

      const updated = await res.json();

      setOrders((prev) =>
        prev.map((o) => (o._id === updated._id ? updated : o))
      );
      setSuccess(
        `Dati contabili aggiornati per la lavorazione ${updated.internalCode}.`
      );
      closeEdit();
    } catch (err) {
      console.error('Errore handleSave (fatture):', err);
      setError(err.message || 'Errore imprevisto nel salvataggio.');
    }
  };

    const openDelete = (order) => {
    setDeleteOrder(order);
    setDeleteCode('');
    setDeleteError('');
  };

  const closeDelete = () => {
    setDeleteOrder(null);
    setDeleteCode('');
    setDeleteError('');
  };

  const handleDelete = async () => {
    if (!deleteOrder) return;

    const expected = deleteOrder.internalCode || '';
    if (deleteCode.trim() !== expected) {
      setDeleteError(
        `Il codice inserito non corrisponde. Inserisci esattamente "${expected}".`
      );
      return;
    }

    try {
      setDeleteError('');
      setError('');
      setSuccess('');

      const res = await authFetch(`${API_BASE}/work/${deleteOrder._id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ internalCodeConfirm: deleteCode.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error || 'Errore durante l\'eliminazione della lavorazione.'
        );
      }

      // rimuovo dal frontend
      setOrders((prev) => prev.filter((o) => o._id !== deleteOrder._id));
      setSuccess(
        `Lavorazione ${expected} eliminata correttamente (commessa e dati contabili).`
      );
      closeDelete();
    } catch (err) {
      console.error('Errore handleDelete (fatture):', err);
      setDeleteError(err.message || 'Errore imprevisto durante l\'eliminazione.');
    }
  };

    const openDeleteDetail = (order, row) => {
    setDeleteDetailOrder(order);
    setDeleteDetailRow(row);
    setDeleteDetailCode('');
    setDeleteDetailError('');
  };

  const closeDeleteDetail = () => {
    setDeleteDetailOrder(null);
    setDeleteDetailRow(null);
    setDeleteDetailCode('');
    setDeleteDetailError('');
  };

  const handleDeleteDetail = async () => {
    if (!deleteDetailOrder || !deleteDetailRow) return;

    const expected = deleteDetailRow.codiceInterno || '';
    if (deleteDetailCode.trim() !== expected) {
      setDeleteDetailError(
        `Il codice inserito non corrisponde. Inserisci esattamente "${expected}".`
      );
      return;
    }

    try {
      setDeleteDetailError('');
      setError('');
      setSuccess('');

      const res = await authFetch(
        `${API_BASE}/work/${deleteDetailOrder._id}/billing-iteration/${deleteDetailRow._id}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ internalCodeConfirm: deleteDetailCode.trim() }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error ||
            'Errore durante l\'eliminazione della riassegnazione.'
        );
      }

      // aggiorno la tabella dei dettagli
      setDetailsRows((prev) =>
        prev.filter((r) => r._id !== deleteDetailRow._id)
      );

      // ricarico le commesse per aggiornare i totali
      await loadOrders();

      setSuccess(
        `Riassegnazione ${expected} eliminata correttamente (importi aggiornati).`
      );
      closeDeleteDetail();
    } catch (err) {
      console.error('Errore handleDeleteDetail (fatture):', err);
      setDeleteDetailError(
        err.message || 'Errore imprevisto durante l\'eliminazione.'
      );
    }
  };



  // -------- IMPORT EXCEL --------
  const handleExcelImport = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  setError('');
  setSuccess('');
  setImporting(true);

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[firstSheetName];

    const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    if (!sheetData || sheetData.length < 2) {
      throw new Error('Il file Excel sembra vuoto o senza righe utili.');
    }

    const headers = sheetData[0];
    const rows = sheetData.slice(1);
    const headerMap = buildHeaderMap(headers);

    if (!headerMap.internalCode) {
      throw new Error(
        'Nel file Excel non è stata trovata una colonna per il codice interno (es. "Codice interno").'
      );
    }

    let okCount = 0;
    let errorCount = 0;

    const toNumberString = (val) => {
      if (val === undefined || val === null || val === '') return undefined;
      const str = String(val).replace(',', '.').trim();
      const n = Number(str);
      if (Number.isNaN(n)) return undefined;
      return String(n);
    };

    const parseExcelDate = (val) => {
      if (!val) return undefined;
      if (val instanceof Date) {
        return val.toISOString().slice(0, 10);
      }
      const str = String(val).trim();
      if (!str) return undefined;

      const partsSlash = str.split('/');
      if (partsSlash.length === 3) {
        const [dd, mm, yyyy] = partsSlash;
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        if (!Number.isNaN(d.getTime())) {
          return d.toISOString().slice(0, 10);
        }
      }
      const d = new Date(str);
      if (!Number.isNaN(d.getTime())) {
        return d.toISOString().slice(0, 10);
      }
      return undefined;
    };

    for (const row of rows) {
      const codeCell = row[headerMap.internalCode];
      if (!codeCell) continue;

      const internalCode = String(codeCell).trim();
      if (!internalCode) continue;

      const payload = { internalCode };

      // ---------- anagrafica commessa ----------
      if (headerMap.externalCode !== undefined) {
        const raw = row[headerMap.externalCode];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.externalCode = String(raw).trim();
        }
      }
      if (headerMap.companyName !== undefined) {
        const raw = row[headerMap.companyName];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.companyName = String(raw).trim();
        }
      }
      if (headerMap.place !== undefined) {
        const raw = row[headerMap.place];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.place = String(raw).trim();
        }
      }
      if (headerMap.equipmentModel !== undefined) {
        const raw = row[headerMap.equipmentModel];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.equipmentModel = String(raw).trim();
        }
      }

      // ---------- importi ----------
      if (headerMap.importoConsuntivo !== undefined) {
        const raw = row[headerMap.importoConsuntivo];
        const n = toNumberString(raw);
        if (n !== undefined) payload.importoConsuntivo = n;
      }
      if (headerMap.importoPreventivo !== undefined) {
        const raw = row[headerMap.importoPreventivo];
        const n = toNumberString(raw);
        if (n !== undefined) payload.importoPreventivo = n;
      }
      if (headerMap.importoOrdine !== undefined) {
        const raw = row[headerMap.importoOrdine];
        const n = toNumberString(raw);
        if (n !== undefined) payload.importoOrdine = n;
      }

      // ---------- campi globali ----------
      if (headerMap.numeroPreventivo !== undefined) {
        const raw = row[headerMap.numeroPreventivo];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.numeroPreventivo = String(raw).trim();
        }
      }
      if (headerMap.riferimentiOrdineCliente !== undefined) {
        const raw = row[headerMap.riferimentiOrdineCliente];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.riferimentiOrdineCliente = String(raw).trim();
        }
      }
      if (headerMap.stato !== undefined) {
        const raw = row[headerMap.stato];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.stato = String(raw).trim();
        }
      }
      if (headerMap.ordineFornitore !== undefined) {
        const raw = row[headerMap.ordineFornitore];
        if (raw !== undefined && raw !== null && raw !== '') {
          payload.ordineFornitore = String(raw).trim();
        }
      }
      if (headerMap.dataOdaFornitura !== undefined) {
        const raw = row[headerMap.dataOdaFornitura];
        const iso = parseExcelDate(raw);
        if (iso) {
          payload.dataOdaFornitura = iso;
        }
      }

      // ---------- chiamata al backend (CREATE+UPDATE) ----------
      try {
        const res = await authFetch(
          `${API_BASE}/work/billing-import-row`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) {
          errorCount++;
        } else {
          okCount++;
        }
      } catch (errReq) {
        console.error('Errore import Excel (row):', errReq);
        errorCount++;
      }
    }

    // ricarico la tabella
    await loadOrders();

    let msg = `Import Excel completato: ${okCount} righe importate.`;
    if (errorCount > 0) {
      msg += ` ${errorCount} righe con errori.`;
    }
    setSuccess(msg);
  } catch (err) {
    console.error('Errore handleExcelImport:', err);
    setError(err.message || "Errore durante l'import da Excel.");
  } finally {
    setImporting(false);
    // per poter ricaricare lo stesso file
    e.target.value = '';
  }
};


  // -------- EXPORT CSV / PDF --------
  const handleExportCsv = () => {
    const header = [
      'Cod. interno',
      'Cod. esterno',
      'Cliente',
      'Luogo',
      'Modello',
      'Stato ordine',
      'Materiale arrivato',
      'Stato preventivo',
      'Imp. consuntivo',
      'Imp. preventivo',
      'N. prev.',
      'Importo ordine',
      'Rif. ordine cliente',
      'Stato (pagamento)',
      'Ordine FOR',
      'Data ODA fornitura',
    ];

    const rows = filteredOrders.map((o) => {
      const b = o.billing || {};
      return [
        o.internalCode || '',
        o.externalCode || '',
        o.companyName || '',
        o.place || '',
        o.equipmentModel || '',
        o.orderStatus || '',
        o.materialeArrivato || '',
        o.preventivoStatus || '',
        formatCurrency(b.importoConsuntivo),
        formatCurrency(b.importoPreventivo),
        b.numeroPreventivo || o.rifPreventivo || '',
        formatCurrency(b.importoOrdine),
        b.riferimentiOrdineCliente || '',
        b.stato || '',
        b.ordineFornitore || '',
        formatDate(b.dataOdaFornitura),
      ];
    });

    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const csv =
      header.map(escape).join(';') +
      '\r\n' +
      rows.map((r) => r.map(escape).join(';')).join('\r\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contabilita_commesse.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    const doc = new jsPDF('l', 'pt');

    const columns = [
      'Cod. interno',
      'Cod. esterno',
      'Cliente',
      'Luogo',
      'Modello',
      'Stato ordine',
      'Materiale arrivato',
      'Stato preventivo',
      'Imp. cons.',
      'Imp. prev.',
      'N. prev.',
      'Imp. ordine',
      'Rif. ordine cliente',
      'Stato pag.',
      'Ordine FOR',
      'Data ODA forn.',
    ];

    const rows = filteredOrders.map((o) => {
      const b = o.billing || {};
      return [
        o.internalCode || '',
        o.externalCode || '',
        o.companyName || '',
        o.place || '',
        o.equipmentModel || '',
        o.orderStatus || '',
        o.materialeArrivato || '',
        o.preventivoStatus || '',
        formatCurrency(b.importoConsuntivo),
        formatCurrency(b.importoPreventivo),
        b.numeroPreventivo || o.rifPreventivo || '',
        formatCurrency(b.importoOrdine),
        b.riferimentiOrdineCliente || '',
        b.stato || '',
        b.ordineFornitore || '',
        formatDate(b.dataOdaFornitura),
      ];
    });

    doc.setFontSize(12);
    doc.text('Situazione contabile commesse', 40, 30);

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 50,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [22, 163, 74] },
    });

    doc.save('contabilita_commesse.pdf');
  };

  // -------- DETTAGLI RIASSEGNAZIONI --------
  const toggleDetails = async (order) => {
    if (expandedOrderId === order._id) {
      setExpandedOrderId(null);
      setDetailsRows([]);
      setDetailsError('');
      return;
    }

    setExpandedOrderId(order._id);
    setDetailsRows([]);
    setDetailsError('');
    setDetailsLoading(true);

    try {
      const res = await authFetch(
        `${API_BASE}/work/${order._id}/billing-details`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.error ||
            'Errore nel caricamento dei dettagli riassegnazioni.'
        );
      }
      const data = await res.json();
      setDetailsRows(data || []);
    } catch (err) {
      console.error('Errore toggleDetails (fatture):', err);
      setDetailsError(
        err.message ||
          'Errore imprevisto nel caricamento delle riassegnazioni.'
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-3 md:px-6 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* HEADER */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
            Gestione fatture e spese
          </h1>
          <p className="text-xs md:text-sm text-gray-500">
            Inserisci e aggiorna i dati contabili per ogni commessa, usando il
            codice interno progressivo.
          </p>
        </Card>

        {/* MESSAGGI */}
        {error && (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-200 px-4 py-3 text-sm text-emerald-700 flex items-center justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
            >
              Chiudi
            </button>
          </div>
        )}

        {/* FILTRI */}
        <Card className="shadow-sm px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Cerca per codice interno, cliente, luogo o modello...
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Digita per filtrare le commesse..."
            />
          </div>
          <div className="w-full md:w-56">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Filtro stato pagamento
            </label>
            <select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
            >
              <option value="">Tutti</option>
              <option value="Da fatturare">Da fatturare</option>
              <option value="Fatturato">Fatturato</option>
              <option value="Pagato">Pagato</option>
              <option value="Stornato">Stornato</option>
            </select>
          </div>
        </Card>

        {/* TABELLA COMMESSE */}
        <Card className="shadow-sm px-3 py-4 md:px-5 md:py-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm md:text-base font-semibold text-gray-800">
              Commesse e dati contabili
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-[11px] text-gray-600 flex items-center gap-1">
                <span>Importa Excel</span>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelImport}
                  disabled={importing}
                  className="text-[10px]"
                />
              </label>
              {importing && (
                <span className="text-[11px] text-emerald-600">
                  Import in corso...
                </span>
              )}
              <button
                onClick={handleExportCsv}
                className="px-3 py-1 rounded-lg border border-gray-300 text-[11px] hover:bg-gray-50"
              >
                Esporta CSV
              </button>
              <button
                onClick={handleExportPdf}
                className="px-3 py-1 rounded-lg border border-gray-300 text-[11px] hover:bg-gray-50"
              >
                Esporta PDF
              </button>
              <span className="text-[11px] text-gray-500">
                Totale: {orders.length}
              </span>
            </div>
          </div>

          {/* LEGENDA */}
          <div className="mb-3 flex flex-wrap gap-3 text-[10px] text-gray-600">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-emerald-400" />
              <span>Ordine ricevuto / Prev. accettato</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-amber-400" />
              <span>Ordine non ricevuto / In attesa prev.</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span>Prev. rifiutato</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full bg-gray-300" />
              <span>Non impostato</span>
            </div>
          </div>

          {loading ? (
            <p className="text-xs text-gray-500">Caricamento commesse...</p>
          ) : filteredOrders.length === 0 ? (
            <p className="text-xs text-gray-500">
              Nessuna commessa trovata con i filtri attuali.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-[11px]">
                <thead className="bg-gray-50">
                  <tr>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Cod. interno
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Cod. esterno
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Cliente
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Luogo
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Modello
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Stato ordine
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Stato preventivo
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Materiale arrivato
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-right">
                      Imp. consuntivo
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-right">
                      Imp. preventivo
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      N. prev.
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-right">
                      Importo ordine
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Rif. ordine cliente
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Stato (pagamento)
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Ordine FOR
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Data ODA fornitura
                    </TableHeadCell>
                    <TableHeadCell className="px-2 py-1 text-left">
                      Azioni
                    </TableHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((o) => {
                    const b = o.billing || {};
                    const isExpanded = expandedOrderId === o._id;

                    const visibleDetails =
                      isExpanded && Array.isArray(detailsRows)
                        ? detailsRows.filter(
                            (d) =>
                              (d.tecnico || '')
                                .trim()
                                .toLowerCase() !== 'da assegnare'
                          )
                        : [];

                    return (
                      <React.Fragment key={o._id}>
                        {/* RIGA PRINCIPALE */}
                        <tr
                          className={`hover:bg-emerald-50 cursor-pointer ${
                            isExpanded ? 'bg-emerald-50/40' : ''
                          }`}
                          onClick={() => toggleDetails(o)}
                        >
                          <TableCell className="px-2 py-1 text-left">
                            <div className="inline-flex items-center gap-1">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[11px] border ${
                                  isExpanded
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                                }`}
                              >
                                {o.internalCode}
                              </span>
                              <span
                                className={`text-[10px] text-gray-400 transition-transform ${
                                  isExpanded ? 'rotate-90' : ''
                                }`}
                              >
                                ▸
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {o.externalCode || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {o.companyName || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {o.place || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {o.equipmentModel || '-'}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {renderOrderStatusBadge(o.orderStatus)}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {renderPreventivoStatusBadge(o.preventivoStatus)}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {o.materialeArrivato === 'Si' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Sì
                              </span>
                            ) : o.materialeArrivato === 'No' ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                No
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right">
                            {formatCurrency(b.importoConsuntivo)}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right">
                            {formatCurrency(b.importoPreventivo)}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {b.numeroPreventivo || o.rifPreventivo || ''}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right">
                            {formatCurrency(b.importoOrdine)}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {b.riferimentiOrdineCliente || ''}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {b.stato || ''}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {b.ordineFornitore || ''}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-left">
                            {formatDate(b.dataOdaFornitura)}
                          </TableCell>
                            <TableCell
                              className="px-2 py-1 text-left"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex gap-1">
                                <button
                                  className="px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[11px] hover:bg-emerald-700"
                                  onClick={() => openEdit(o, null)}
                                >
                                  Modifica
                                </button>
                                <button
                                  className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[11px] hover:bg-red-700"
                                  onClick={() => openDelete(o)}
                                >
                                  Elimina
                                </button>
                              </div>
                            </TableCell>                              
                        </tr>

                        {/* RIGA ESPANSA CON REITERAZIONI */}
                        {isExpanded && (
                          <tr className="bg-gray-50">
                            <TableCell
                              colSpan={16}
                              className="px-3 py-2"
                            >
                              {detailsLoading ? (
                                <p className="text-xs text-gray-500">
                                  Caricamento riassegnazioni...
                                </p>
                              ) : detailsError ? (
                                <p className="text-xs text-red-600">
                                  {detailsError}
                                </p>
                              ) : visibleDetails.length === 0 ? (
                                <p className="text-xs text-gray-500">
                                  Nessuna riassegnazione con tecnico diverso da
                                  &quot;Da Assegnare&quot;.
                                </p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-[11px]">
                                    <thead>
                                      <tr className="bg-gray-100">
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Cod. interno
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Data intervento
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Tecnico
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Stato ordine
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Materiale arrivato
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Stato preventivo
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-right">
                                          Imp. consuntivo
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-right">
                                          Imp. preventivo
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          N. prev.
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-right">
                                          Importo ordine
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Rif. ordine cliente
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Stato (pagamento)
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Ordine FOR
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Data ODA fornitura
                                        </TableHeadCell>
                                        <TableHeadCell className="px-2 py-1 text-left">
                                          Azioni
                                        </TableHeadCell>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {visibleDetails.map((r) => (
                                        <tr
                                          key={r._id}
                                          className="hover:bg-white cursor-pointer"
                                          onClick={() => openEdit(o, r)}
                                        >
                                          <TableCell className="px-2 py-1 text-left font-mono">
                                            {r.codiceInterno || '-'}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {formatDate(r.dataIntervento)}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {r.tecnico || '-'}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {renderOrderStatusBadge(
                                              o.orderStatus
                                            )}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {o.materialeArrivato === 'Si' ? (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                Sì
                                              </span>
                                            ) : o.materialeArrivato === 'No' ? (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                                No
                                              </span>
                                            ) : (
                                              <span className="text-gray-400 text-xs">-</span>
                                            )}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {renderPreventivoStatusBadge(
                                              o.preventivoStatus
                                            )}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-right">
                                            {formatCurrency(
                                              r.importoConsuntivo
                                            )}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-right">
                                            {formatCurrency(
                                              r.importoPreventivo
                                            )}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {r.numeroPreventivo || o.rifPreventivo || ''}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-right">
                                            {formatCurrency(r.importoOrdine)}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {r.riferimentiOrdineCliente || ''}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {r.stato || ''}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {r.ordineFornitore || ''}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            {formatDate(r.dataOdaFornitura)}
                                          </TableCell>
                                          <TableCell className="px-2 py-1 text-left">
                                            <div className="flex gap-1">
                                              <button
                                                className="px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[11px] hover:bg-emerald-700"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openEdit(o, r);
                                                }}
                                              >
                                                Modifica
                                              </button>
                                              <button
                                                className="px-2 py-0.5 rounded-md bg-red-600 text-white text-[11px] hover:bg-red-700"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openDeleteDetail(o, r);
                                                }}
                                              >
                                                Elimina
                                              </button>
                                            </div>
                                          </TableCell>
                                              
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </TableCell>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* MODALE MODIFICA DATI CONTABILI */}
        {editingOrder && (
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-40">
            <Card className="shadow-xl w-full max-w-lg p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm md:text-base font-semibold text-gray-800">
                    Modifica dati contabili – {editingIterationCode}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Aggiorna gli importi e lo stato della commessa.
                  </p>
                </div>
                <button
                  onClick={closeEdit}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  Chiudi
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-xs">
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Importo consuntivo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={billingForm.importoConsuntivo}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        importoConsuntivo: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Importo preventivo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={billingForm.importoPreventivo}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        importoPreventivo: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Numero preventivo
                  </label>
                  <input
                    type="text"
                    value={billingForm.numeroPreventivo}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        numeroPreventivo: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Importo ordine
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={billingForm.importoOrdine}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        importoOrdine: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block font-semibold text-gray-600 mb-1">
                    Riferimenti ordine cliente
                  </label>
                  <input
                    type="text"
                    value={billingForm.riferimentiOrdineCliente}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        riferimentiOrdineCliente: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Stato (pagamento)
                  </label>
                  <select
                    value={billingForm.stato}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        stato: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">-</option>
                    <option value="Da fatturare">Da fatturare</option>
                    <option value="Fatturato">Fatturato</option>
                    <option value="Pagato">Pagato</option>
                    <option value="Stornato">Stornato</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Materiale arrivato
                  </label>
                  <select
                    value={billingForm.materialeArrivato}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        materialeArrivato: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    <option value="">-</option>
                    <option value="Si">Sì</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Ordine FOR
                  </label>
                  <input
                    type="text"
                    value={billingForm.ordineFornitore}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        ordineFornitore: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-gray-600 mb-1">
                    Data ODA fornitura
                  </label>
                  <input
                    type="date"
                    value={billingForm.dataOdaFornitura}
                    onChange={(e) =>
                      setBillingForm((f) => ({
                        ...f,
                        dataOdaFornitura: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={closeEdit}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                >
                  Salva
                </button>
              </div>
            </Card>
          </div>
        )}

                {/* MODALE ELIMINAZIONE LAVORAZIONE */}
        {deleteOrder && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <Card className="shadow-xl w-full max-w-md p-5">
              <h3 className="text-base font-semibold text-gray-800 mb-2">
                Elimina lavorazione {deleteOrder.internalCode}
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Questa operazione eliminerà la commessa, i dati contabili e le
                eventuali riassegnazioni collegate. L&apos;azione è
                irreversibile.
              </p>
              <p className="text-xs text-gray-700 mb-2">
                Per confermare, digita esattamente il codice interno{' '}
                <span className="font-mono font-semibold">
                  {deleteOrder.internalCode}
                </span>{' '}
                nel campo sottostante.
              </p>

              <input
                type="text"
                value={deleteCode}
                onChange={(e) => setDeleteCode(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-2"
                placeholder={deleteOrder.internalCode}
              />

              {deleteError && (
                <p className="text-xs text-red-600 mb-2">{deleteError}</p>
              )}

              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={closeDelete}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleDelete}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700"
                >
                  Elimina definitivamente
                </button>
              </div>
            </Card>
          </div>
        )}
                {/* MODALE ELIMINAZIONE SINGOLA RIASSEGNAZIONE */}
        {deleteDetailOrder && deleteDetailRow && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <Card className="shadow-xl w-full max-w-md p-5">
              <h3 className="text-base font-semibold text-gray-800 mb-2">
                Elimina riassegnazione {deleteDetailRow.codiceInterno}
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Questa operazione eliminerà SOLO questa iterazione/rias-segnazione
                e gli importi collegati. I totali della commessa verranno
                aggiornati automaticamente.
              </p>
              <p className="text-xs text-gray-700 mb-2">
                Per confermare, digita esattamente il codice interno{' '}
                <span className="font-mono font-semibold">
                  {deleteDetailRow.codiceInterno}
                </span>{' '}
                nel campo sottostante.
              </p>

              <input
                type="text"
                value={deleteDetailCode}
                onChange={(e) => setDeleteDetailCode(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-2"
                placeholder={deleteDetailRow.codiceInterno}
              />

              {deleteDetailError && (
                <p className="text-xs text-red-600 mb-2">
                  {deleteDetailError}
                </p>
              )}

              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={closeDeleteDetail}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleDeleteDetail}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700"
                >
                  Elimina iterazione
                </button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default GestioneFatturazione;
